import { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { MdPlayArrow } from "react-icons/md";
import { getApiErrorMessage } from "@/hooks/useApiErrorToast";
import CopyButton from "@/components/consignado/Cobranca/VinculosAtivosRedesign/CopyButton";
import {
  CatalogoDisparo,
  ContextoDisparo,
  PreviewBuscaDto,
  PreviewResultado,
  PropriedadeCatalogo,
  TipoPropriedade,
} from "@/models/consignado/disparo.model";
import { FiltroDraft } from "./disparo-draft";
import { GrupoNo, condicoesDaArvore, paraDtoArvore } from "./filtro-arvore";
import { propriedade as buscarPropriedade, colunasDaPrevia, propriedadesDoContexto } from "./catalogo.helpers";
import FiltroMiniatura from "./FiltroMiniatura";
import { fieldFont } from "./styleTokens";

type Props = {
  contexto: ContextoDisparo;
  /** A mesma árvore que o cadastro monta — a prévia roda a busca dela. */
  filtros: GrupoNo;
  catalogo?: CatalogoDisparo;
  disabled?: boolean;
  /**
   * POST /disparos/preview. Sem isso o modal mostra uma amostra simulada
   * no mesmo formato da resposta — o protótipo não chama a API.
   */
  onPreview?: (dto: PreviewBuscaDto) => Promise<PreviewResultado>;
};

/** Propriedades que a prévia traz quando o cadastro não pede nada específico. */
const PADRAO = ["id", "status", "criadoEm"];

const LIMITE = 5;

// Colunas de código curto (`codigoCurto`, `proposta.codigoCurto`,
// `propostaLeilaoCodigoCurto`) — o short_hash, que se pesquisa no banco.
const ehShorthash = (coluna: string) => /codigoCurto$/i.test(coluna);

const NOMES_FICTICIOS = ["Maria Silva", "João Santos", "Ana Oliveira", "Carlos Souza", "Fernanda Lima"];

// Hash simples (djb2) + PRNG linear — determinístico a partir do filtro
// atual, pra a amostra não trocar a cada reabertura sem o usuário ter
// mudado nada.
const hashString = (s: string): number => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i);
  return h >>> 0;
};

const criarGeradorSeeded = (seed: number) => {
  let x = seed || 1;
  return () => {
    x = (x * 1664525 + 1013904223) >>> 0;
    return x / 4294967296;
  };
};

const valorFicticio = (
  p: PropriedadeCatalogo | undefined,
  rand: () => number,
  filtros: FiltroDraft[],
): string => {
  // Se o filtro fixou o valor da propriedade, a amostra respeita — é o que
  // a busca de verdade devolveria.
  const fixado = filtros.find((f) => f.propriedade === p?.id)?.valores.find((v) => v.trim() !== "");
  if (fixado) return fixado;
  if (!p) return "—";
  switch (p.tipo) {
    case TipoPropriedade.DATA:
      return new Date(Date.now() - Math.floor(rand() * 90) * 86400000).toISOString().slice(0, 10);
    case TipoPropriedade.NUMERO:
      return (Math.floor(rand() * 900000) / 100).toFixed(2);
    case TipoPropriedade.CPF:
      return "123.456.789-00";
    case TipoPropriedade.CNPJ:
      return "12.345.678/0001-00";
    case TipoPropriedade.EMAIL:
      return "contato@exemplo.com.br";
    case TipoPropriedade.TELEFONE:
      return "(11) 99999-0000";
    default:
      if (p.valores?.length) return p.valores[Math.floor(rand() * p.valores.length)];
      if (p.id.endsWith("nome")) return NOMES_FICTICIOS[Math.floor(rand() * NOMES_FICTICIOS.length)];
      return `${p.id}-${Math.floor(rand() * 9000 + 1000)}`;
  }
};

const previewSimulado = (
  dto: PreviewBuscaDto,
  catalogo: CatalogoDisparo | undefined,
): PreviewResultado => {
  const propriedades = dto.propriedades?.length ? dto.propriedades : PADRAO;
  const disponiveis = propriedadesDoContexto(catalogo, dto.contexto);
  const rand = criarGeradorSeeded(hashString(JSON.stringify(dto)));
  const quantidade = Math.max(1, Math.floor(rand() * (dto.limite ?? LIMITE)) + 1);

  const amostra = Array.from({ length: quantidade }, () => {
    const linha: Record<string, unknown> = {};
    propriedades.forEach((id) => {
      linha[id.replace(/\./g, "_")] = valorFicticio(
        disponiveis.find((p) => p.id === id),
        rand,
        [],
      );
    });
    return linha;
  });

  return {
    contexto: dto.contexto,
    filtrosAplicados: dto.filtros,
    propriedades,
    limite: dto.limite ?? LIMITE,
    quantidadeRetornada: amostra.length,
    amostra,
    execucao: false,
  };
};

// Prévia da busca que o cron usaria — mesmo corpo do POST /disparos/preview
// (contexto + filtros + limite + propriedades) e mesma resposta. Nunca
// envia mensagem: `execucao` vem false, por contrato da 007.
export default function TestarBuscaModal({
  contexto,
  filtros,
  catalogo,
  disabled,
  onPreview,
}: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [resultado, setResultado] = useState<PreviewResultado | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);

  const condicoes = condicoesDaArvore(filtros);
  const propriedades = colunasDaPrevia(catalogo, contexto);
  const dto: PreviewBuscaDto = {
    contexto,
    filtros: paraDtoArvore(filtros),
    limite: LIMITE,
    ...(propriedades.length ? { propriedades } : {}),
  };

  const handleOpen = async () => {
    onOpen();
    setCarregando(true);
    setErro(null);
    setResultado(null);
    try {
      setResultado(onPreview ? await onPreview(dto) : previewSimulado(dto, catalogo));
    } catch (e) {
      // Sem isto, a busca que falha fica igual à busca que não achou nada —
      // e "nenhum registro" é resposta, não erro.
      setErro(getApiErrorMessage(e, "Não foi possível consultar a prévia."));
    } finally {
      setCarregando(false);
    }
  };

  const colunas = resultado?.propriedades ?? [];

  return (
    <>
      <Button
        leftIcon={<MdPlayArrow />}
        variant="outline"
        colorScheme="secondary"
        size="sm"
        alignSelf="flex-start"
        isDisabled={disabled || condicoes.length === 0}
        onClick={handleOpen}
        {...fieldFont}
      >
        Testar busca
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader {...fieldFont}>Prévia da busca</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Flex align="center" gap={2} mb={3} wrap="wrap">
              <Tag size="sm" colorScheme="gray" {...fieldFont}>
                {resultado?.quantidadeRetornada ?? 0} de até {resultado?.limite ?? LIMITE}
              </Tag>
              {!onPreview && (
                <Tag size="sm" colorScheme="orange" {...fieldFont}>
                  Amostra simulada
                </Tag>
              )}
              <Tag size="sm" colorScheme="purple" {...fieldFont}>
                Não envia mensagem
              </Tag>
            </Flex>

            <Text fontSize="xs" color="gray.500" mb={3} {...fieldFont}>
              Mesma busca que a rotina faria, limitada a {LIMITE} registros.
            </Text>

            {carregando ? (
              <Text fontSize="sm" color="gray.500" {...fieldFont}>
                Consultando…
              </Text>
            ) : erro ? (
              <Flex align="center" gap={1.5} bg="red.50" borderRadius="md" px={3} py={2}>
                <WarningTwoIcon boxSize={3} color="red.500" flexShrink={0} />
                <Text fontSize="sm" color="red.600" {...fieldFont}>
                  {erro}
                </Text>
              </Flex>
            ) : resultado && resultado.amostra.length > 0 ? (
              <TableContainer borderWidth="1px" borderColor="gray.200" borderRadius="md">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      {colunas.map((coluna) => (
                        <Th key={coluna} fontSize="10px" fontFamily="Lexend Deca">
                          {ehShorthash(coluna)
                            ? "Shorthash"
                            : buscarPropriedade(catalogo, contexto, coluna)?.descricao ?? coluna}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {resultado.amostra.map((linha, i) => (
                      <Tr key={i}>
                        {colunas.map((coluna) => {
                          const valor = linha[coluna.replace(/\./g, "_")];
                          const texto = String(valor ?? "—");
                          return (
                            <Td key={coluna} fontSize="xs" {...fieldFont}>
                              {ehShorthash(coluna) && valor != null && valor !== "" ? (
                                <Flex align="center" gap={1}>
                                  {texto}
                                  <CopyButton value={texto} rotulo="shorthash" />
                                </Flex>
                              ) : (
                                texto
                              )}
                            </Td>
                          );
                        })}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : (
              <Text fontSize="sm" color="gray.500" {...fieldFont}>
                Nenhum registro atende a esses filtros.
              </Text>
            )}

            <Box mt={4}>
              <Text fontSize="xs" color="gray.500" mb={1} {...fieldFont}>
                Filtros aplicados
              </Text>
              <Box bg="gray.50" borderRadius="md" p={2}>
                <FiltroMiniatura grupo={filtros} contexto={contexto} catalogo={catalogo} />
              </Box>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
